/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useMemo } from 'react';
import { useNavigation, useParams, useSearchParams } from 'react-router';
import type { ShopperProducts } from '@/scapi';
import { readVariantIdFromPath } from '@/lib/product/product-utils';

interface UseSelectedVariationsParams {
    product: ShopperProducts.schemas['Product'];
    isChildProduct?: boolean;
    /**
     * Optional override that takes precedence over URL search params. When provided, this hook
     * uses these values as the per-attribute selections instead of reading from `useSearchParams`.
     * Defaults (representedProduct → first orderable variant → single-value attribute) still
     * apply for any attribute the override doesn't supply.
     *
     * Use this in modal contexts where swatch selections must be ephemeral and not pollute the
     * page URL or trigger route revalidation (e.g. cart-edit / quick-add modals containing
     * bundle/set children).
     */
    selectionsOverride?: Record<string, string>;
}

/**
 * Hook to get currently selected variation values, with fallback to product defaults.
 *
 * Selection sources are consulted in priority order:
 *   1. `selectionsOverride` (modal contexts where selections must not touch the URL)
 *   2. The variant SKU named by the URL's `/product/<master>/<sku>` path segment (UCR-3032) — the
 *      canonical statement of which variant is on screen, resolved against `product.variants`
 *   3. The canonical URL search params (`useSearchParams`), which carry partial selections on
 *      multi-attribute masters
 *   4. Product defaults (`variationValues` → fallback variant → single-value attributes)
 *
 * Sources 2 and 3 are both read from the destination of an in-flight navigation
 * (`useNavigation().location`) when there is one, rather than the settled URL — that yields
 * optimistic readings while a swatch click's nav is settling, so consumers see the user's choice on
 * the next paint.
 *
 * @param params - Configuration object
 * @param params.product - Product containing variation attributes and optional default variationValues
 * @param params.isChildProduct - Whether this product is a child product (part of set/bundle, default: false)
 * @returns Object of selected variations with attribute IDs as keys
 *
 * @example
 * // URL: /?color=NAVYWL&size=040&someOtherParam=ignored
 * const product = {
 *   variationAttributes: [
 *     { id: 'color', name: 'Color', values: [...] },
 *     { id: 'size', name: 'Size', values: [...] }
 *   ]
 * }
 *
 * const selections = useSelectedVariations({ product });
 * // Returns: { color: 'NAVYWL', size: '040' }
 * // Note: 'someOtherParam' is ignored because it's not a variation attribute and
 * default val will be used if url param for that attribute does not exist
 *
 * @example
 * // URL: / (no selections, no defaults)
 * const masterProduct = { variationAttributes: [...] }
 * const selections = useSelectedVariations({ product: masterProduct });
 * // Returns: {}
 * // Note: Empty object when no variations are selected and no defaults
 *
 * @example
 * // Child product within a bundle/set with nested URL parameters
 * // URL: /?childProduct456=color%3DRED%26size%3DL&otherParam=value
 * const childProduct = { id: 'childProduct456', variationAttributes: [...] }
 * const selections = useSelectedVariations({ product: childProduct, isChildProduct: true });
 * // Returns: { color: 'RED', size: 'L' }
 * // Note: Extracts and decodes nested parameters for individual products within bundles/sets
 *
 * @example
 * // URL: /?color=NAVY (canonical), navigation in flight to /?color=RED
 * const selections = useSelectedVariations({ product });
 * // Returns: { color: 'RED' }
 * // Note: While the click navigation is pending, the destination's params are preferred so
 * // the swatch's `selected` flag reflects the user's choice immediately.
 */
export const useSelectedVariations = ({
    product,
    isChildProduct = false,
    selectionsOverride,
}: UseSelectedVariationsParams) => {
    const [searchParams] = useSearchParams();
    const navigation = useNavigation();
    const routeParams = useParams();

    return useMemo(() => {
        if (!product?.variationAttributes) return {};

        // Optimistic swatch activation: while a navigation triggered by clicking a swatch is in
        // flight, prefer the destination URL's params over the canonical URL so the clicked
        // swatch reflects as selected on the next paint instead of waiting for the loader chain
        // (color/size click → pid sync → SCAPI roundtrip) to settle. Same pattern the PLP uses
        // for refines — see docs/README-STATE.md "Via useNavigation()" and the precedent in
        // category-refinements/index.tsx. `useNavigation()` is global — it reflects any in-flight
        // navigation, not just swatch clicks. Acceptable trade-off: a cross-page nav (e.g. PDP→PDP
        // via a related-product link) unmounts this hook before the brief deselection paints.
        const pendingSearchParams = navigation.location ? new URLSearchParams(navigation.location.search) : undefined;
        const effectiveSearchParams = pendingSearchParams ?? searchParams;

        // The variant SKU named by the URL path (UCR-3032), read pending-nav-first for the same
        // optimistic reason as the search params above: a swatch click on a single-attribute product
        // navigates straight to `/product/<master>/<sku>` with no query string, so without this the
        // clicked swatch would not light up until the loader returned the new variant.
        //
        // Child products in a set/bundle are excluded — the path variant belongs to the parent. The
        // `variants` lookup is itself a guard: an id that is not one of this product's variants
        // contributes nothing and the hook falls through to the query-param sources below.
        const effectiveVariantId = isChildProduct
            ? undefined
            : navigation.location
              ? readVariantIdFromPath(navigation.location.pathname)
              : routeParams.variantId;
        const pathVariantValues = effectiveVariantId
            ? product.variants?.find((variant) => variant.productId === effectiveVariantId)?.variationValues
            : undefined;

        // Source for current selections: caller-provided override (modal contexts), the URL's variant
        // path segment, then pending-nav-aware URL params (PDP).
        const getSelected = (attributeId: string): string | undefined => {
            if (selectionsOverride) {
                return selectionsOverride[attributeId];
            }
            if (isChildProduct) {
                // For child products (individual products within bundles/sets): params are nested
                // like ?childProductId=color%3DRED%26size%3DL
                const productParamsString = effectiveSearchParams.get(product.id) || '';
                return new URLSearchParams(productParamsString).get(attributeId) || undefined;
            }
            // The path segment is the canonical statement of which variant is shown, so it outranks
            // any leftover attribute query param.
            return pathVariantValues?.[attributeId] || effectiveSearchParams.get(attributeId) || undefined;
        };

        // For master products that lack their own variationValues (e.g. set/bundle children),
        // fall back to a merchant-configured variant when SCAPI provides one via representedProduct.
        // representedProduct isn't in the typed Product schema (only ShopperSearch hits) but the
        // schema is open so SCAPI may include it. Read defensively via the open extension.
        const representedProductId = (product as { representedProduct?: { id?: string } }).representedProduct?.id;
        const representedVariant =
            !product.variationValues && representedProductId
                ? product.variants?.find((v) => v.productId === representedProductId)
                : undefined;

        // For child products in sets/bundles where merchants didn't pre-configure defaults
        // (no variationValues, no representedProduct), pick the first orderable variant's
        // variationValues as a coherent default — this guarantees auto-selected attributes
        // are mutually compatible (avoids selecting a color+size combo that isn't orderable).
        const firstOrderableChildVariant =
            isChildProduct && !product.variationValues && !representedVariant
                ? (product.variants?.find((v) => v.orderable) ?? product.variants?.[0])
                : undefined;

        const fallbackVariant = representedVariant ?? firstOrderableChildVariant;

        // Build object of currently selected variation values from the override/URL with fallback to defaults
        // (the product's default variationValues for variant products, then variant fallbacks).
        const result = product?.variationAttributes?.reduce(
            (selections, attribute) => {
                // First priority: caller override (modal) or URL param (PDP) for this attribute
                const explicitValue = getSelected(attribute.id);

                // Second priority: product's default variationValues (for variant products)
                // Third priority: a fallback variant's values (representedProduct hint or, for child
                //   products without merchant defaults, the first orderable variant — keeps the
                //   selected (color, size, ...) tuple internally consistent)
                // Fourth priority: when an attribute has only ONE possible value, auto-select it
                const onlyValue =
                    attribute.values && attribute.values.length === 1 ? attribute.values[0]?.value : undefined;
                const defaultValue =
                    product.variationValues?.[attribute.id] ??
                    fallbackVariant?.variationValues?.[attribute.id] ??
                    onlyValue;

                // Use explicit value if available, otherwise use default, otherwise skip this attribute
                const value = explicitValue || defaultValue;

                // This keeps the returned object clean - no undefined/null values
                return value ? { ...selections, [attribute.id]: value } : selections;
            },
            {} as Record<string, string>
        );

        return result;
    }, [product, searchParams, navigation.location, routeParams.variantId, isChildProduct, selectionsOverride]);
};
